import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, of } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';

import { SOURCE_TYPE } from '../constants/Tpiq.constants';
import {
  BasicPlanLimit,
  BasicPlanUpgradeSource,
  PlanCreationConfirmation
} from '../models/basic-plan-limit.model';
import { TaxplanIQServices } from '../services/TaxplanIQServices';
import { Session } from '../services/Session';
import { FeatureFlagService } from './feature-flag.service';

// Keep these keywords and their order in step with ProductEntitlementService.classifyPlanningTier.
const NON_BASIC_PLAN_KEYWORDS = ['growth', 'ccta', 'student', 'standard', 'professional', 'asap', 'groplan'];

/** Reads the backend error code from a Basic plan limit error. */
export function basicPlanLimitErrorCode(error: any): string | undefined {
  return basicPlanLimitErrorPayload(error)?.code;
}

/** Finds the backend error body in an HTTP error, a response body, or a JSON string. */
export function basicPlanLimitErrorPayload(error: any): any {
  if (!error) {
    return undefined;
  }
  if (typeof error === 'string') {
    try {
      return basicPlanLimitErrorPayload(JSON.parse(error));
    } catch (_) {
      return undefined;
    }
  }
  if (error.code || error.message && !error.error) {
    return error;
  }
  return basicPlanLimitErrorPayload(error.error);
}

/** Loads Basic plan limits for the header and plan creation workflows. */
@Injectable({ providedIn: 'root' })
export class BasicPlanLimitService extends TaxplanIQServices {
  private readonly cache = new Map<string, Observable<BasicPlanLimit>>();
  private readonly invalidatedSubject = new Subject<{ orgId?: string; taxYear?: number }>();
  readonly invalidated$ = this.invalidatedSubject.asObservable();

  constructor(http: HttpClient, private readonly featureFlags: FeatureFlagService) {
    super(http);
  }

  get(orgId: string, userId: string, taxYear: number, clientId?: string,
      forceRefresh = false, authoritative = false): Observable<BasicPlanLimit> {
    if (!authoritative && !this.featureFlags.isEnabled('enableBasicPlanLimits')) {
      return of(this.unrestricted(taxYear));
    }

    const cacheKey = `${orgId}:${taxYear}:${clientId || ''}`;
    if (forceRefresh) {
      this.cache.delete(cacheKey);
    }
    const cached = this.cache.get(cacheKey);
    if (cached) {
      return cached;
    }

    let url = `/TaxPlanningServices/orgs/${orgId}/users/${userId}/basic-plan-limits?taxYear=${taxYear}`;
    if (clientId) {
      url += `&clientId=${encodeURIComponent(clientId)}`;
    }
    const request = this.query(url, SOURCE_TYPE.JAVA).pipe(shareReplay(1));
    this.cache.set(cacheKey, request);
    return request;
  }

  newConfirmation(entitlement: BasicPlanLimit): PlanCreationConfirmation {
    return {
      creationRequestId: uuid(),
      confirmedCreditNumber: entitlement.nextCredit
    };
  }

  requestUpgrade(orgId: string, userId: string, source: BasicPlanUpgradeSource): Observable<any> {
    const url = `/TaxPlanningServices/orgs/${orgId}/users/${userId}/basic-plan-limits/upgrade-requests`;
    return this.create(url, {source}, SOURCE_TYPE.JAVA);
  }

  /** True when the planning subscription name saved at login is a Basic plan. */
  isBasicSessionPlan(): boolean {
    let plan = '';
    try {
      plan = String(Session.getUserAccessInfo()?.subscriptionPlan || '').toLowerCase();
    } catch (_) {
      return false;
    }
    if (NON_BASIC_PLAN_KEYWORDS.some(keyword => plan.includes(keyword))) {
      return false;
    }
    return plan.includes('basic');
  }

  /**
   * Decides jAIne and Discovery when the limits request fails. Missing data must not remove
   * features, so only a Basic firm or a known inactive subscription loses them.
   */
  featuresAllowedOnError(error: any): boolean {
    return basicPlanLimitErrorCode(error) !== 'PLANNING_SUBSCRIPTION_INACTIVE' && !this.isBasicSessionPlan();
  }

  invalidate(orgId?: string, taxYear?: number): void {
    if (!orgId) {
      this.cache.clear();
      this.invalidatedSubject.next({});
      return;
    }
    const prefix = taxYear ? `${orgId}:${taxYear}:` : `${orgId}:`;
    Array.from(this.cache.keys())
      .filter(key => key.startsWith(prefix))
      .forEach(key => this.cache.delete(key));
    this.invalidatedSubject.next({orgId, taxYear});
  }

  private unrestricted(taxYear: number): BasicPlanLimit {
    return {
      tier: 'UNKNOWN',
      basicRestrictionsActive: false,
      taxYear,
      used: 0,
      limit: 12,
      nextCredit: 1,
      canCreate: true,
      canDelete: true,
      jaineAllowed: true,
      discoveryAllowed: true
    };
  }
}
